package com.arena_web.ui.layouts

import androidx.compose.runtime.*
import com.arena_web.di.LocalAppContainer
import com.arena_web.ui.components.business.NoClubsEmptyState
import com.arena_web.ui.components.navigation.BusinessBottomNavBar
import com.arena_web.ui.components.navigation.BusinessSidebarTab
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.sections.header.BusinessHeader
import com.arena_web.ui.sections.header.MobileBusinessTopBar
import com.arena_web.ui.theme.LocalSportColors
import com.arena_web.ui.theme.SportColors
import com.arena_web.ui.utils.*
import com.arena_web.utils.Storage
import com.arena_web.utils.StorageKeys
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.ColumnScope
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*

/** Provides the currently selected club ID to all business pages. */
val LocalSelectedClubId = compositionLocalOf<String?> { null }

@Composable
fun BusinessPageLayout(
    activeTab: BusinessSidebarTab? = null,
    showEmptyStateWhenNoClubs: Boolean = true,
    content: @Composable ColumnScope.() -> Unit
) {
    LaunchedEffect(Unit) {
        Storage.save(StorageKeys.ACTIVE_ROLE, "BUSINESS")
    }

    val container = LocalAppContainer.current
    val t = LocalAppStrings.current
    val mobile = isMobile()

    // Load clubs once — cached in AppContainer, survives navigation
    LaunchedEffect(Unit) {
        container.ensureClubsLoaded()
    }

    val clubs by container.clubs.collectAsState()
    val selectedClubId by container.selectedClubId.collectAsState()
    val clubsLoaded by container.clubsLoaded.collectAsState()

    CompositionLocalProvider(
        LocalSportColors provides SportColors.Football,
        LocalSelectedClubId provides selectedClubId
    ) {
        if (mobile) {
            // Mobile: Coming Soon layout — body composite shows through.
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(100.vh)
                    .overflow(Overflow.Hidden)
            ) {
                Column(
                    modifier = Modifier
                        .fillMaxSize()
                        .padding(top = 12.px, leftRight = 16.px)
                        .gap(16.px)
                ) {
                    // Top bar
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .styleModifier { property("flex-shrink", "0") }
                    ) {
                        MobileBusinessTopBar(
                            clubs = clubs,
                            selectedClubId = selectedClubId,
                            onClubSelected = { container.selectClub(it) },
                            onAddClub = { container.selectClub(null) }
                        )
                    }

                    // Coming Soon placeholder
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .flexGrow(1)
                            .minHeight(0.px)
                            .padding(bottom = 96.px),
                        contentAlignment = Alignment.Center
                    ) {
                        SpanText(
                            text = t.layoutComingSoon,
                            modifier = Modifier
                                .fontSize(28.px)
                                .fontWeight(FontWeight.Bold)
                                .color(Color.white)
                        )
                    }
                }

                // Bottom navigation bar
                BusinessBottomNavBar(activeTab = activeTab)
            }
        } else {
            // Desktop layout
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(100.vh)
                    .overflow(Overflow.Hidden)
            ) {
                Column(
                    modifier = Modifier.fillMaxSize()
                ) {
                    // Header area
                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .flexShrink(0)
                            .sPadding(30, 40)
                            .sPaddingBottom(0)
                    ) {
                        BusinessHeader(
                            activeTab = activeTab,
                            clubs = clubs,
                            selectedClubId = selectedClubId,
                            onClubSelected = { container.selectClub(it) },
                            onAddClub = { container.selectClub(null) }
                        )
                    }

                    // Dynamic content
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .flexGrow(1)
                            .minHeight(0.px)
                            .overflow { y(Overflow.Auto) }
                    ) {
                        if (showEmptyStateWhenNoClubs && clubsLoaded && selectedClubId == null) {
                            NoClubsEmptyState()
                        } else {
                            content()
                        }
                    }
                }
            }
        }
    }
}
